import React, { useRef, useState } from "react";
import { Editor } from "@tinymce/tinymce-react";
import { Box, CircularProgress } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import axiosInstance from "api/axiosInstance";
import { formatContentAsHTML } from "api/openai/openaiAPI";

// Default plugin/toolbar sets. Exported so callers that need a richer configuration (the
// CANmail HTML email editor) can extend rather than duplicate them, e.g.
// `plugins={[...DEFAULT_PLUGINS, "preview"]}`.
export const DEFAULT_PLUGINS = [
  "advlist",
  "autolink",
  "lists",
  "link",
  "image",
  "searchreplace",
  "code",
  "fullscreen",
  "table",
  "wordcount",
  "nonbreaking",
  "textpattern",
  "codesample",
];

export const DEFAULT_TOOLBAR =
  "aiformat | undo redo | bold italic underline strikethrough | " +
  "forecolor backcolor | alignleft aligncenter alignright alignjustify | " +
  "bullist numlist outdent indent | " +
  "link image | removeformat | code fullscreen";

const CONTENT_STYLE_LIGHT =
  'body { font-family: "Roboto", "Helvetica", "Arial", sans-serif; font-size: 14px; line-height: 1.5; color: rgba(0, 0, 0, 0.87); }';
const CONTENT_STYLE_DARK =
  'body { font-family: "Roboto", "Helvetica", "Arial", sans-serif; font-size: 14px; line-height: 1.5; background-color: #222; color: #ccc; }';

/**
 * Shared TinyMCE rich-text editor.
 *
 * The defaults reproduce the original blog-post behaviour exactly, so existing callers
 * that pass only `value`/`onChange` (blog, cases, comments, cantrack) are unaffected. The
 * extra props exist for the CANmail HTML email editor, which needs a different
 * toolbar/plugin set, its own (public, absolute) image upload, direct access to the
 * editor instance for variable insertion, and an always-light editing surface.
 *
 * @param {(blob: Blob, filename: string) => Promise<string>} [uploadHandler]
 *        Returns the URL to embed. Defaults to the built-in bucket upload keyed off
 *        `imageVisibility`. The email caller injects an absolute/public uploader here.
 * @param {string[]} [plugins]  TinyMCE plugin list override. Defaults to `DEFAULT_PLUGINS`.
 * @param {string} [toolbar]    Toolbar override. Defaults to `DEFAULT_TOOLBAR` (minus the
 *                              AI button when `showAIFormat` is false).
 * @param {(editor: any) => void} [onEditorInit]  Called once the editor instance is ready.
 * @param {boolean} [showAIFormat=true]  Register/show the "AI Format" button.
 * @param {boolean} [forceLightContent=false]  Keep the editing surface light even in dark
 *        mode (the chrome still follows the theme). Correct for email authoring, where
 *        recipients render the body on their own background, not the author's.
 * @param {object} [extraInit]  Extra editor options, merged last.
 */
function RichTextEditor({
  value = "",
  onChange,
  height = 300,
  placeholder = "Start typing...",
  disabled = false,
  imageVisibility = "private",
  uploadHandler,
  plugins,
  toolbar,
  onEditorInit,
  showAIFormat = true,
  forceLightContent = false,
  extraInit,
}) {
  const editorRef = useRef(null);
  const [isFormatting, setIsFormatting] = useState(false);
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  // Chrome (skin/frame/borders) always follows the theme; only the editable content area
  // can be pinned light via `forceLightContent`.
  const contentIsDark = isDark && !forceLightContent;

  // Built-in bucket upload. Returns a RELATIVE URL — fine for in-app rendering (blog,
  // cases, comments), which is exactly why the email caller injects its own absolute,
  // public `uploadHandler` rather than relying on this.
  const uploadToFileBucket = async (blob, filename) => {
    const formData = new FormData();
    formData.append("file", blob, filename);
    formData.append("is_public", imageVisibility === "public" ? "true" : "false");

    const { data } = await axiosInstance.post("/api3.2/bucket/upload", formData);
    if (imageVisibility === "public") {
      return `/api3.2/noauth/bucket/${data.id}`;
    }
    return `/api3.2/bucket/${data.id}`;
  };

  const uploadImage = uploadHandler ?? uploadToFileBucket;

  const imagesUploadHandler = (blobInfo, progress) => {
    return new Promise((resolve, reject) => {
      uploadImage(blobInfo.blob(), blobInfo.filename() || `image-${Date.now()}.png`)
        .then((url) => {
          progress(100);
          resolve(url);
        })
        .catch((error) => {
          console.error("[RichTextEditor] Image upload failed:", error);
          reject({ message: error.message, remove: true });
        });
    });
  };

  const handleAIFormat = async () => {
    const editor = editorRef.current;
    if (!editor) return;

    const content = editor.getContent({ format: "text" });
    if (!content || content.trim().length < 10) {
      editor.notificationManager.open({
        text: "Add more content before formatting with AI.",
        type: "warning",
        timeout: 3000,
      });
      return;
    }

    setIsFormatting(true);
    const notification = editor.notificationManager.open({
      text: "AI is formatting your content...",
      type: "info",
      closeButton: false,
    });

    try {
      const html = await formatContentAsHTML(content);
      editor.setContent(html);
      if (onChange) onChange(html);
      notification.close();
      editor.notificationManager.open({
        text: "Content formatted successfully.",
        type: "success",
        timeout: 3000,
      });
    } catch (error) {
      console.error("[RichTextEditor] AI format failed:", error);
      notification.close();
      editor.notificationManager.open({
        text: "AI formatting failed. Please try again.",
        type: "error",
        timeout: 5000,
      });
    } finally {
      setIsFormatting(false);
    }
  };

  // Consumer-supplied toolbars are used verbatim; only the default toolbar drops the AI
  // button when it is disabled, so a caller that opts out never sees an unknown-button warning.
  const effectiveToolbar =
    toolbar ?? (showAIFormat ? DEFAULT_TOOLBAR : DEFAULT_TOOLBAR.replace("aiformat | ", ""));

  return (
    <Box
      sx={{
        position: "relative",
        '& .tox-tinymce': {
          borderColor: isDark ? 'rgba(255, 255, 255, 0.23)' : 'rgba(0, 0, 0, 0.23)',
          borderRadius: '4px',
          transition: 'border-color 0.2s',
          '&:hover': {
            borderColor: isDark ? 'rgba(255, 255, 255, 0.87)' : 'rgba(0, 0, 0, 0.87)',
          },
        },
        '& .tox .tox-toolbar, & .tox .tox-toolbar__overflow, & .tox .tox-toolbar__primary': {
          background: 'transparent',
        },
        '& .tox:not(.tox-tinymce-inline) .tox-editor-header': {
          borderBottom: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)'}`,
          boxShadow: 'none',
        },
        '& .tox .tox-statusbar': {
          borderTop: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)'}`,
        },
      }}
    >
      {isFormatting && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            zIndex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "rgba(0,0,0,0.3)",
            borderRadius: 1,
          }}
        >
          <CircularProgress size={32} />
        </Box>
      )}
      <Editor
        apiKey={import.meta.env.VITE_TINYMCE_API_KEY}
        onInit={(evt, editor) => {
          editorRef.current = editor;
          onEditorInit?.(editor);
        }}
        value={value}
        onEditorChange={(content) => onChange?.(content)}
        disabled={disabled}
        init={{
          height,
          menubar: false,
          placeholder,
          statusbar: true,
          elementpath: false,
          branding: false,
          promotion: false,
          skin: isDark ? "oxide-dark" : "oxide",
          content_css: contentIsDark ? "dark" : "default",
          plugins: plugins ?? DEFAULT_PLUGINS,
          toolbar: effectiveToolbar,
          toolbar_mode: "floating",
          setup: (editor) => {
            if (!showAIFormat) return;
            editor.ui.registry.addIcon(
              "ai-sparkle",
              '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">' +
                '<path d="M12 3l1.5 4.5L18 9l-4.5 1.5L12 15l-1.5-4.5L6 9l4.5-1.5z"/>' +
                '<path d="M19 13l.75 2.25L22 16l-2.25.75L19 19l-.75-2.25L16 16l2.25-.75z"/>' +
                '<path d="M5 17l.5 1.5L7 19l-1.5.5L5 21l-.5-1.5L3 19l1.5-.5z"/>' +
                "</svg>"
            );
            editor.ui.registry.addButton("aiformat", {
              icon: "ai-sparkle",
              tooltip: "AI Format — clean up and structure content",
              onAction: handleAIFormat,
            });
          },
          text_patterns: [
            { start: "*", cmd: "InsertUnorderedList", trigger: "space" },
            { start: "-", cmd: "InsertUnorderedList", trigger: "space" },
            { start: "1.", cmd: "InsertOrderedList", trigger: "space" },
            { start: "#", format: "h1", trigger: "space" },
            { start: "##", format: "h2", trigger: "space" },
            { start: "###", format: "h3", trigger: "space" },
            { start: "####", format: "h4", trigger: "space" },
            { start: ">", cmd: "mceBlockQuote", trigger: "space" },
            { start: "---", replacement: "<hr/>" },
          ],
          nonbreaking_force_tab: true,
          browser_spellcheck: true,
          content_style: contentIsDark ? CONTENT_STYLE_DARK : CONTENT_STYLE_LIGHT,
          paste_data_images: true,
          automatic_uploads: true,
          images_upload_handler: imagesUploadHandler,
          images_reuse_filename: true,
          file_picker_types: "image",
          file_picker_callback: (callback, value, meta) => {
            if (meta.filetype !== "image") return;
            const input = document.createElement("input");
            input.type = "file";
            input.accept = "image/*";
            input.onchange = function () {
              const file = this.files[0];
              if (file) {
                uploadImage(file, file.name)
                  .then((url) => callback(url, { title: file.name }))
                  .catch((error) => {
                    console.error("[RichTextEditor] Image upload failed:", error);
                    editorRef.current?.notificationManager?.open({
                      text: "Image upload failed. Please try again.",
                      type: "error",
                      timeout: 5000,
                    });
                  });
              }
            };
            input.click();
          },
          ...extraInit,
        }}
      />
    </Box>
  );
}

export default RichTextEditor;
